import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { syncService } from '@/services/syncService';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const SyncStatusIndicator = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleSyncEvent = (event) => {
      switch (event.type) {
        case 'online':
          setIsOnline(true);
          toast.success('Network connection restored. Syncing data...');
          break;
        case 'offline':
          setIsOnline(false);
          toast.warning('Network connection lost. Working offline.');
          break;
        case 'sync_start':
          setSyncStatus({ type: 'syncing' });
          break;
        case 'sync_progress':
          setSyncStatus({
            type: 'syncing',
            success: event.success,
            failed: event.failed,
            total: event.total
          });
          break;
        case 'sync_complete':
          setSyncStatus({
            type: 'complete',
            success: event.success,
            failed: event.failed,
            total: event.total
          });
          if (event.success > 0) {
            toast.success(
              <div>
                <p className="font-semibold">Sync Complete</p>
                <p className="text-sm mt-1">{event.success} entries synced successfully</p>
              </div>
            );
          }
          if (event.failed > 0) {
            toast.error(`${event.failed} entries failed to sync`);
          }
          setTimeout(() => setSyncStatus(null), 5000);
          break;
        case 'sync_error':
          setSyncStatus({ type: 'error' });
          toast.error('Sync failed. Will retry automatically.');
          setTimeout(() => setSyncStatus(null), 5000);
          break;
        default:
          break;
      }
    };

    syncService.addListener(handleSyncEvent);

    return () => {
      syncService.removeListener(handleSyncEvent);
    };
  }, []);

  const handleManualSync = async () => {
    if (!isOnline) {
      toast.warning('Cannot sync while offline');
      return;
    }
    await syncService.syncAll();
  };

  if (!syncStatus) return null;

  return (
    <div
      className="fixed bottom-20 right-4 bg-white border-2 border-[#E5DFD0] rounded-lg shadow-lg p-4 max-w-sm z-50"
      data-testid="sync-status-indicator"
    >
      {syncStatus.type === 'syncing' && (
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-[#2D5F4F] animate-spin" />
          <div>
            <p className="font-medium text-[#1A1F2E]">Syncing Data...</p>
            {syncStatus.total > 0 && (
              <p className="text-sm text-[#5A5F6E] mt-0.5">
                {syncStatus.success} of {syncStatus.total} entries synced
              </p>
            )}
          </div>
        </div>
      )}

      {syncStatus.type === 'complete' && (
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-[#2D5F4F]" />
          <div>
            <p className="font-medium text-[#1A1F2E]">Sync Complete</p>
            <p className="text-sm text-[#5A5F6E] mt-0.5">
              {syncStatus.success} entries synced
              {syncStatus.failed > 0 && `, ${syncStatus.failed} failed`}
            </p>
          </div>
        </div>
      )}

      {syncStatus.type === 'error' && (
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-[#B8374A]" />
          <div className="flex-1">
            <p className="font-medium text-[#1A1F2E]">Sync Failed</p>
            <p className="text-sm text-[#5A5F6E] mt-0.5">Will retry automatically</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleManualSync}
            className="h-8"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default SyncStatusIndicator;